import ExcelJS from "exceljs";

function safeDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-IN");
}

function safeFileName(value) {
  return String(value || "trip")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "_")
    .slice(0, 80) || "trip";
}

function normalizedDuplicateKey(expense) {
  const date = safeDate(expense.expense_date).trim().toLowerCase();
  const category = String(expense.category || "").trim().toLowerCase();
  const description = String(expense.description || "").trim().replace(/\s+/g, " ").toLowerCase();
  const amount = Number(expense.amount || 0).toFixed(2);
  return [date, category, description, amount].join("|");
}

function approvalStatus(expense) {
  if (!expense.cash_bill_approval_id) return "Not required / not raised";
  if (expense.cash_bill_manager_status === "declined" || expense.cash_bill_finance_status === "declined") return "Declined";
  if (expense.cash_bill_finance_status === "approved") return "Fully approved";
  if (expense.cash_bill_manager_status === "approved") return "Manager approved; Finance pending";
  if (expense.cash_bill_manager_status === "pending") return "Manager approval pending";
  return "Review pending";
}

function approvalStage(expense, stage) {
  if (!expense.cash_bill_approval_id) return "Not required / not raised";
  const value = stage === "manager"
    ? expense.cash_bill_manager_status
    : expense.cash_bill_finance_status;
  if (value === "approved") return "Approved";
  if (value === "declined") return "Declined";
  if (value === "pending") return "Pending";
  return "Not recorded";
}

function sharingDetails(expense) {
  const details = [];
  if (Number(expense.food_total_people_count) > 0) details.push(`${expense.food_total_people_count} people`);
  if (Number(expense.food_external_people_count) > 0) details.push(`${expense.food_external_people_count} external`);
  if (Number(expense.food_pack_count) > 0) details.push(`${expense.food_pack_count} food packs`);
  return details.join("; ");
}

const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F766E" } };
const headerFont = { bold: true, color: { argb: "FFFFFFFF" } };
const currencyFormat = '₹#,##0.00';

function styleHeader(row) {
  row.eachCell(cell => {
    cell.fill = headerFill;
    cell.font = headerFont;
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });
  row.height = 30;
}

export function buildTripExpenseWorkbook(trip, expenses = []) {
  const employeeExpenses = expenses.filter(expense => expense.payment_source !== "company" && Number(expense.employee_reimbursable ?? 1) !== 0);
  const companyPaidExpenses = expenses.filter(expense => expense.payment_source === "company" || Number(expense.employee_reimbursable ?? 1) === 0);
  const employeeTotal = employeeExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const companyTotal = companyPaidExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const grandTotal = employeeTotal + companyTotal;
  const advance = Number(trip.advance_amount || 0);
  const duplicateCounts = expenses.reduce((counts, expense) => {
    const key = normalizedDuplicateKey(expense);
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Ebek One";
  workbook.created = new Date();

  const summary = workbook.addWorksheet("Trip Summary", {
    properties: { defaultRowHeight: 20 },
    views: [{ state: "normal" }],
  });
  summary.columns = [{ width: 27 }, { width: 46 }];
  [
    ["Trip Expense Statement", ""],
    ["Trip", trip.title || ""],
    ["Employee", trip.employee_name || trip.name || ""],
    ["Purpose", trip.purpose || ""],
    ["Destination", trip.destination || ""],
    ["Start date", safeDate(trip.start_date)],
    ["End date", safeDate(trip.end_date)],
    ["Status", trip.status || ""],
    ["Submitted", safeDate(trip.submitted_at)],
    ["", ""],
    ["Employee expenses", employeeTotal],
    ["Company-paid costs", companyTotal],
    ["Total trip cost", grandTotal],
    ["Advance issued", advance],
    [advance - employeeTotal >= 0 ? "Refund due from employee" : "Amount due to employee", Math.abs(advance - employeeTotal)],
  ].forEach(values => summary.addRow(values));
  summary.mergeCells("A1:B1");
  summary.getCell("A1").font = { bold: true, size: 15, color: { argb: "FF0F172A" } };
  summary.getCell("A1").alignment = { vertical: "middle" };
  summary.getRow(1).height = 28;
  for (let rowNumber = 11; rowNumber <= 15; rowNumber += 1) {
    summary.getCell(rowNumber, 2).numFmt = currencyFormat;
  }
  summary.getRow(13).font = { bold: true };
  summary.getRow(15).font = { bold: true };

  const details = workbook.addWorksheet("Expense Details", {
    properties: { defaultRowHeight: 20 },
    // Freeze only the header. Avoid the legacy split-pane metadata that
    // prevented some Excel clients from scrolling through all detail rows.
    views: [{ state: "frozen", ySplit: 1, activeCell: "A2" }],
  });
  const headers = [
    "#", "Date", "Category", "Subcategory / Type", "Description", "Payment source", "Bill type",
    "From", "To", "KM travelled", "Hotel name", "Hotel location", "Vehicle number", "Odometer start",
    "Odometer end", "Courier company", "Weight", "No. of boxes", "Tracking / Booking ref", "Bill reference",
    "Manager approval", "Finance approval", "Overall approval status",
    "Sharing / headcount", "Duplicate check", "Amount (INR)",
  ];
  const widths = [6, 13, 18, 23, 38, 18, 16, 24, 24, 14, 22, 22, 18, 15, 15, 22, 13, 13, 24, 17, 22, 22, 29, 24, 27, 16];
  details.columns = headers.map((header, index) => ({ header, key: `column${index + 1}`, width: widths[index] }));
  styleHeader(details.getRow(1));

  expenses.forEach((expense, index) => {
    const detailRow = details.addRow([
      index + 1,
      safeDate(expense.expense_date),
      expense.category || "",
      expense.expense_subcategory || expense.food_subcategory || expense.travel_subcategory || expense.courier_subtype || expense.print_subcategory || "",
      expense.description || "",
      expense.payment_source === "company" || Number(expense.employee_reimbursable ?? 1) === 0 ? "Company paid" : "Employee expense",
      expense.bill_type || "",
      expense.travel_from || expense.courier_from || "",
      expense.travel_to || expense.courier_to || "",
      expense.cab_kms_travelled || expense.kms_travelled || "",
      expense.hotel_name || "",
      expense.hotel_location || "",
      expense.fuel_vehicle_no || "",
      expense.odometer_start || "",
      expense.odometer_end || "",
      expense.courier_name || "",
      expense.courier_weight || "",
      expense.courier_boxes || "",
      expense.courier_tracking_ref || expense.booking_reference || "",
      expense.bill_url ? "Available in app" : "Not attached",
      approvalStage(expense, "manager"),
      approvalStage(expense, "finance"),
      approvalStatus(expense),
      sharingDetails(expense),
      duplicateCounts[normalizedDuplicateKey(expense)] > 1 ? "CHECK — possible duplicate" : "",
      Number(expense.amount || 0),
    ]);
    detailRow.alignment = { vertical: "top", wrapText: true };
    detailRow.getCell(26).numFmt = currencyFormat;
  });

  const totalRow = details.addRow([...Array(24).fill(""), "Total trip cost", grandTotal]);
  totalRow.getCell(25).fill = headerFill;
  totalRow.getCell(25).font = headerFont;
  totalRow.getCell(26).font = { bold: true };
  totalRow.getCell(26).numFmt = currencyFormat;
  details.autoFilter = { from: "A1", to: "Z1" };

  return workbook;
}

export async function downloadTripExpenseExcel(trip, expenses) {
  const workbook = buildTripExpenseWorkbook(trip, expenses);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safeFileName(trip.title)}_expense_statement.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
